import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LAYERS } from "@/lib/layers";
import { useGame } from "@/context/GameContext";
import { t } from "@/lib/translations";
import { playSfx, startMusic, stopMusic } from "@/lib/sounds";
import { api } from "@/lib/api";
import {
  setResearchEnabled,
  setChildContext,
  setGameContext,
  clearGameContext,
  logGameStart,
  logGameWin,
  logGameExit,
  flush as flushResearch,
} from "@/lib/research";
import TopBar from "@/components/TopBar";
import Companion from "@/components/Companion";
import StarReward from "@/components/StarReward";
import ScientificDialog from "@/components/ScientificDialog";
import { BookOpen, Lock } from "lucide-react";
import { Layer1Games } from "@/games/Layer1Games";
import { Layer2Games } from "@/games/Layer2Games";
import { Layer3Games } from "@/games/Layer3Games";
import { Layer4Games } from "@/games/Layer4Games";
import { Layer5Games } from "@/games/Layer5Games";
import bg1 from "@/assets/backgrounds/bg_layer1.png";
import bg2 from "@/assets/backgrounds/bg_layer2.png";
import bg3 from "@/assets/backgrounds/bg_layer3.png";
import bg4 from "@/assets/backgrounds/bg_layer4.png";
import bg5 from "@/assets/backgrounds/bg_layer5.png";
import PaywallModal from "@/components/PaywallModal";
import ParentGate from "@/components/ParentGate";

const LAYER_BACKGROUNDS = { 1: bg1, 2: bg2, 3: bg3, 4: bg4, 5: bg5 };

const GAMES_BY_LAYER = {
  1: Layer1Games,
  2: Layer2Games,
  3: Layer3Games,
  4: Layer4Games,
  5: Layer5Games,
};

export default function PlayRoom() {
  const { age } = useParams();
  const ageNum = parseInt(age, 10);
  const layer = LAYERS[ageNum];
  const navigate = useNavigate();
  const { language, soundOn, musicOn, addStars, activeChildId, researchMode, hasFullAccess, unlockedLayers } = useGame();

  const [activeGame, setActiveGame] = useState(null); // null = menu
  const [showReward, setShowReward] = useState(false);
  const [scienceFor, setScienceFor] = useState(null);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  useEffect(() => {
    if (!layer) {
      navigate("/");
      return;
    }
    if (musicOn) startMusic();
    return () => stopMusic();
  }, [layer, musicOn, navigate]);

  // Sync research mode + child context
  useEffect(() => {
    setResearchEnabled(researchMode);
    if (researchMode && activeChildId) setChildContext(activeChildId);
  }, [researchMode, activeChildId]);

  // Log game_start / game_exit transitions
  useEffect(() => {
    if (!researchMode || !activeChildId) return;
    if (activeGame) {
      setGameContext(ageNum, activeGame);
      logGameStart({ age: ageNum });
      return () => {
        logGameExit({ age: ageNum });
        clearGameContext();
        flushResearch();
      };
    }
  }, [activeGame, ageNum, researchMode, activeChildId]);

  if (!layer) return null;

  const games = GAMES_BY_LAYER[ageNum];

  const handleWin = async (gameKey) => {
    playSfx("star", soundOn);
    addStars(1);
    setShowReward(true);
    logGameWin({ age: ageNum, game: gameKey });
    if (activeChildId) {
      try {
        await api.recordProgress({
          child_id: activeChildId,
          layer: ageNum,
          game_key: gameKey,
          stars_earned: 1,
        });
      } catch (err) {
        console.warn("Failed to record progress:", err);
      }
    }
  };

  const handlePlayAgain = () => {
    setShowReward(false);
  };

  const handleBackToMenu = () => {
    setShowReward(false);
    setActiveGame(null);
  };

  return (
    <div
      className="min-h-screen relative overflow-x-hidden overflow-y-auto grain bg-parallax"
      style={{
        backgroundImage: `url(${LAYER_BACKGROUNDS[ageNum] || bg1})`,
        backgroundSize: "cover",
        backgroundPosition: "center bottom",
        backgroundRepeat: "no-repeat",
      }}
      data-testid={`playroom-${ageNum}`}
    >
      <div className="absolute inset-0 bg-white/30 backdrop-blur-[2px] z-0" />
      <div className="relative z-10">
        <TopBar themeColor={layer.color} title={t(layer.themeKey, language)} />

      <div className="max-w-6xl mx-auto px-4 sm:px-8 pb-12 relative">
        <AnimatePresence mode="wait">
          {activeGame === null && (
            <motion.div
              key="menu"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center mt-2"
            >
              {/* Companion */}
              <div className="flex flex-col items-center md:col-span-1">
                <Companion
                  age={ageNum}
                  imageUrl={layer.baby}
                  themeColor={layer.color}
                />
                <div
                  className="mt-4 px-5 py-2 rounded-full bg-white font-display font-bold text-lg shadow-lg"
                  style={{ color: layer.colorDark }}
                  data-testid="companion-name"
                >
                  {t(layer.companionKey, language)}
                </div>
              </div>

              {/* Games list */}
              <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
                {layer.games.map((gKey, idx) => {
                  const GameDef = games[gKey];
                  if (!GameDef) return null;
                  const isLayerUnlocked = hasFullAccess || unlockedLayers.includes(ageNum);
                  const isLocked = idx > 0 && !isLayerUnlocked;

                  return (
                    <motion.div
                      key={gKey}
                      initial={{ opacity: 0, scale: 0.85 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.1 + idx * 0.1, type: "spring", damping: 12 }}
                      className="relative"
                    >
                      <motion.button
                        whileHover={isLocked ? {} : { y: -6 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          if (isLocked) {
                            playSfx("pop", soundOn);
                            setPendingAction("paywall");
                            setGateOpen(true);
                          } else {
                            playSfx("pop", soundOn);
                            setActiveGame(gKey);
                          }
                        }}
                        className={`toy-btn w-full rounded-3xl p-5 sm:p-6 text-white flex flex-col items-center justify-center min-h-[180px] gap-3 relative overflow-hidden ${
                          isLocked ? "opacity-75 grayscale-[30%] cursor-pointer" : ""
                        }`}
                        style={{
                          background: isLocked ? "#94A3B8" : layer.color,
                          boxShadow: `0 8px 0 ${isLocked ? "#475569" : layer.colorDark}`,
                          border: "4px solid white",
                        }}
                        data-testid={`game-card-${gKey}`}
                      >
                        {/* Lock Overlay Badge */}
                        {isLocked && (
                          <div className="absolute top-3 left-3 bg-amber-400 text-slate-950 p-1.5 rounded-full border-2 border-white shadow-md z-20">
                            <Lock className="w-3.5 h-3.5 fill-current" />
                          </div>
                        )}
                        <div className="text-5xl sm:text-6xl">{GameDef.icon}</div>
                        <div className="font-display font-bold text-lg sm:text-xl text-center leading-tight">
                          {t(`g_${gKey}`, language)}
                          {isLocked && (
                            <span className="block text-[11px] font-extrabold tracking-wider text-amber-300 uppercase mt-1">
                              {language === "id" ? "Premium 👑" : "Premium 👑"}
                            </span>
                          )}
                        </div>
                      </motion.button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setScienceFor(gKey);
                        }}
                        className="absolute top-2 right-2 w-9 h-9 rounded-full bg-white/95 hover:bg-white shadow-md flex items-center justify-center"
                        style={{ color: layer.colorDark }}
                        data-testid={`science-badge-${gKey}`}
                        aria-label="Scientific basis"
                        title={language === "id" ? "Dasar Ilmiah" : "Scientific Basis"}
                      >
                        <BookOpen className="w-4 h-4" strokeWidth={3} />
                      </button>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {activeGame !== null && games[activeGame] && (
            <motion.div
              key={activeGame}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="mt-2"
            >
              {/* Render game */}
              {(() => {
                const G = games[activeGame].Component;
                return (
                  <G
                    layer={layer}
                    onWin={() => handleWin(activeGame)}
                    onBack={() => setActiveGame(null)}
                  />
                );
              })()}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <StarReward
        show={showReward}
        themeColor={layer.color}
        onAgain={handlePlayAgain}
        onClose={handleBackToMenu}
      />

        <ScientificDialog
          open={!!scienceFor}
          gameKey={scienceFor}
          themeColor={layer.color}
          onClose={() => setScienceFor(null)}
        />

        <PaywallModal
          isOpen={paywallOpen}
          onClose={() => setPaywallOpen(false)}
        />

        <ParentGate
          open={gateOpen}
          onClose={() => {
            setGateOpen(false);
            setPendingAction(null);
          }}
          onPass={() => {
            setGateOpen(false);
            if (pendingAction === "paywall") {
              setPaywallOpen(true);
            }
            setPendingAction(null);
          }}
        />
      </div>
    </div>
  );
}
